/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { ArrowBackIcon, AddIcon } from '@chakra-ui/icons';
import ReusableTable from 'components/ReusableTable';
import DiseaseProtocolForm from './diseaseProtocalForm';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useDebounce from 'constants/helper/useDebounce';
import { useDiseaseProtocolList } from './hooks/useDiseaseProtocolList';
import { useUploadMasterFile } from './hooks/useUploadMasterFile';
import { useUploadedMasterList } from './hooks/useUploadedMasterList';
import { getSystemListApi } from 'api/auth.api';
import { useMasterErrorLogs } from './hooks/useMasterErrorLogs';

function DiseaseProtocol() {
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState('');
  const debouncedSearch = useDebounce(searchString, 500);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedMaster, setSelectedMaster] = useState('');
  const [selectedAxis, setSelectedAxis] = useState('');
  const [sortOrder, setSortOrder] = useState(''); // '', 'asc', 'desc'

  const queryClient = useQueryClient();
  const { diseaseProtocol, totalPages, isLoading } = useDiseaseProtocolList(
    page,
    debouncedSearch,
    selectedAxis,
    sortOrder,
  );

  const tableData = diseaseProtocol?.map((item, index) => ({
    ...item,
    srNo: (page - 1) * 20 + index + 1,
  }));

  const { mutate: uploadFile } = useUploadMasterFile();

  const handleUpload = (file) => {
    if (!file) return;
    uploadFile(file);
  };

  const { masterListData } = useUploadedMasterList();
  const { masterErrorLogs } = useMasterErrorLogs();

  const mappedMasterList = React.useMemo(() => {
    return (
      masterListData?.map((item) => ({
        label: item.fileName,
        value: item.fileName,
      })) || []
    );
  }, [masterListData]);

  const { data: systemData } = useQuery({
    queryKey: ['systems'],
    queryFn: getSystemListApi,
  });

  const axisList = systemData?.data?.result || [];

  const mappedAxisList = React.useMemo(() => {
    return (
      axisList?.map((item) => {
        if (typeof item === 'object' && item !== null) {
          return {
            label: item.system || item.name || JSON.stringify(item),
            value: item.system || item._id || JSON.stringify(item),
          };
        }
        return {
          label: item,
          value: item,
        };
      }) || []
    );
  }, [axisList]);

  const handleFilterChange = (value) => {
    setSelectedAxis(value ?? '');
  };

  const handleSearch = (value) => {
    setSearchString(value);
  };

  return (
    <Box p={4} mt={10}>
      {!showForm && (
        <ReusableTable
          data={tableData}
          columns={[
            { key: 'srNo', label: 'Sr.No', width: '80px', headerSize: '14px' },
            {
              key: 'avgReliefPercent',
              label: 'Avg Relief %',
              width: '100px',
              headerSize: '14px',
              sortable: true,
            },
            {
              key: 'system',
              label: 'System English',
              width: '500px',
              headerSize: '14px',
            },
            {
              key: 'systemHi',
              label: 'System Hindi',
              width: '500px',
              headerSize: '14px',
            },
            {
              key: 'diseaseEnglish',
              label: 'Disease Name English',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'diseaseHindi',
              label: 'Disease Name Hindi',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'bottle_1_primary_functional',
              label: 'bottle 1 primary functional',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'bottle_1_supportive',
              label: 'bottle 1 supportive',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'bottle_1_E',
              label: 'bottle 1 Electricity',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'bottle_2_primary_functional',
              label: 'bottle 2 primary functional',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'bottle_2_supportive',
              label: 'bottle 2 supportive',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'bottle_2_E',
              label: 'bottle 2 Electricity',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'bottle_3_chronic',
              label: 'Bottle 3 chronic',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'bottle_3_supportive',
              label: 'bottle 3 supportive',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'bottle_3_E',
              label: 'bottle 3 Electricity',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'bottle_4_degeneration',
              label: 'Bottle 4 Degeneration',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'bottle_4_supportive',
              label: 'bottle 4 supportive',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'bottle_4_E',
              label: 'bottle 4 Electricity',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'state',
              label: 'State',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'temperament',
              label: 'Constitution',
              width: '300px',
              headerSize: '14px',
            },
            {
              key: 'product',
              label: 'Product',
              width: '300px',
              headerSize: '14px',
            },
          ]}
          errorLogData={masterErrorLogs}
          page={page}
          totalPages={totalPages || 1}
          showSearch={true}
          loading={isLoading}
          onRowClick={() => {}}
          onEdit={(id) => {
            setSelectedId(id);
            setShowForm(true);
          }}
          onPageChange={(newPage) => setPage(newPage)}
          showTopAction={true}
          topAction={{
            title: 'Disease Protocol Page',
            label: 'Add Protocol',
            icon: <AddIcon />,
            onClick: () => setShowForm(true),
          }}
          showViewToggle={false}
          showUpload
          showFilter
          showCommonFilter={true}
          commonFilterOptions={mappedAxisList}
          commonFilterValue={selectedAxis}
          onCommonFilterChange={handleFilterChange}
          filterOptions={mappedMasterList}
          onFilterChange={(value) => {
            setSelectedMaster(value);
          }}
          filterValue={selectedMaster}
          onUpload={handleUpload}
          onSearch={handleSearch}
          sortKey="avgReliefPercent"
          sortOrder={sortOrder}
          onSort={(key) => {
            if (key === 'avgReliefPercent') {
              setSortOrder((prev) => {
                if (prev === '') return 'high';
                if (prev === 'high') return 'low';
                return '';
              });
            }
          }}
        />
      )}

      {showForm && (
        <Box w="100%">
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            gap={4}
            mb={4}
            ml={3}
          >
            <Text fontSize="22px" fontWeight="bold">
              Disease Protocol
            </Text>
            <Button
              variant="ghost"
              leftIcon={<ArrowBackIcon />}
              onClick={() => {
                setShowForm(false);
                setSelectedId(null);
              }}
            >
              Back
            </Button>
          </Flex>

          <DiseaseProtocolForm
            setSelectedId={setSelectedId}
            axisList={axisList}
            editId={selectedId}
            onSuccess={() => {
              queryClient.invalidateQueries(['diseaseProtocolList']);
              setShowForm(false);
            }}
          />
        </Box>
      )}
    </Box>
  );
}

export default DiseaseProtocol;
